// src/components/admin/AdminOverview.jsx
// All data fetched from GET /admin/stats/overview via adminApi
// ✅ Fully mobile-responsive (≤960px, ≤768px, ≤480px)

import { useState, useEffect } from 'react';
import { adminApi } from '../../utils/api';

// ── helpers ───────────────────────────────────────────────────
const fmt$ = n => `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

function MCard({ label, value, sub, color = 'var(--gold)', badge, delta }) {
  return (
    <div className="mcard" style={{ '--mc-color': color }}>
      <div className="mcard-label">
        {label}
        {badge && (
          <span className="mcard-badge" style={{ background: `${color}18`, color }}>
            {badge}
          </span>
        )}
      </div>
      <div className="mcard-val" style={{ color }}>{value}</div>
      {sub && <div className="mcard-sub">{sub}</div>}
      {delta && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 8 }}>
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: '.04em',
            color: delta.up ? 'var(--green)' : 'var(--red)',
            background: delta.up ? 'rgba(46,204,138,.12)' : 'rgba(217,79,79,.12)',
            padding: '2px 7px', borderRadius: 3,
          }}>
            {delta.up ? '↑' : '↓'} {delta.text}
          </span>
        </div>
      )}
    </div>
  );
}

function Sparkline({ data = [], color = 'var(--gold)', height = 44 }) {
  if (!data.length) return null;
  const w = 200, h = height;
  const min = Math.min(...data), max = Math.max(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => [
    (i / (data.length - 1)) * w,
    h - ((v - min) / range) * (h - 6) - 3,
  ]);
  const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const fill = `${d} L${w},${h} L0,${h} Z`;
  const gid = `sg${color.replace(/[^a-z]/gi, '')}`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: '100%', height, display: 'block' }}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity=".18"/>
          <stop offset="100%" stopColor={color} stopOpacity="0"/>
        </linearGradient>
      </defs>
      <path d={fill} fill={`url(#${gid})`}/>
      <path d={d} fill="none" stroke={color} strokeWidth="1.5"/>
    </svg>
  );
}

function LoadingCard() {
  return (
    <div className="mcard" style={{ opacity: .5 }}>
      <div className="mcard-label">Loading…</div>
      <div className="mcard-val" style={{ color: 'var(--gray)' }}>—</div>
    </div>
  );
}

// ── Paid now live feed (last 5 paid users from /admin/users) ──
function PaidRow({ name, plan, amount, time, color }) {
  const initials = name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  return (
    <div style={{ display:'flex', alignItems:'center', gap:12, padding:'11px 0', borderBottom:'1px solid rgba(255,255,255,.04)' }}>
      <div style={{ width:32, height:32, borderRadius:6, flexShrink:0, background:color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, color:'var(--black)' }}>{initials}</div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:13, fontWeight:600, color:'var(--white)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{name}</div>
        <div style={{ fontSize:11, color:'var(--gray)', marginTop:1 }}>{plan}</div>
      </div>
      <div style={{ textAlign:'right', flexShrink:0 }}>
        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:'var(--green)', letterSpacing:'.03em' }}>{amount}</div>
        <div style={{ fontSize:10, color:'var(--gray)', marginTop:1 }}>{time}</div>
      </div>
    </div>
  );
}

const AVATAR_COLORS = ['var(--gold)', 'var(--blue)', 'var(--green)', '#f472b6', '#a78bfa'];

// ── Main component ────────────────────────────────────────────
export default function AdminOverview() {
  const [stats,     setStats]     = useState(null);
  const [paidUsers, setPaidUsers] = useState([]);
  const [loadingS,  setLoadingS]  = useState(true);
  const [loadingU,  setLoadingU]  = useState(true);
  const [error,       setError]       = useState(null);
  const [chartType,   setChartType]   = useState('line'); // 'line' | 'bar' | 'area'

  useEffect(() => {
    adminApi.getOverview()
      .then(data => setStats(data))
      .catch(err  => setError(err.message))
      .finally(()  => setLoadingS(false));

    adminApi.getUsers({ plan: 'paid', limit: 5 })
      .then(data => setPaidUsers(data.users || []))
      .catch(() => {})
      .finally(() => setLoadingU(false));

    // activeUsers now comes directly from the overview API response (server-counted)
    // No need for the wasteful limit:9999 fetch anymore.
  }, []);

  if (error) {
    return (
      <div style={{ padding: 40, color: 'var(--red)', fontSize: 14 }}>
        Failed to load overview: {error}
      </div>
    );
  }

  const growthValues = stats?.growthByMonth ? Object.values(stats.growthByMonth) : [];
  const growthLabels = stats?.growthByMonth ? Object.keys(stats.growthByMonth)   : [];

  const winPct = stats
    ? Math.round((stats.paidUsers / (stats.totalUsers || 1)) * 100)
    : 0;

  return (
    <div className="ov-page">

      {/* ── METRIC CARDS ── */}
      <div className="ov-metric-row">
        {loadingS ? (
          Array.from({ length: 5 }).map((_, i) => <LoadingCard key={i}/>)
        ) : (
          <>
            <MCard
              label="Total Users" color="var(--gold)" badge="ALL TIME"
              value={stats.totalUsers.toLocaleString()}
              sub={`${stats.freeUsers.toLocaleString()} free · ${stats.paidUsers.toLocaleString()} paid · ${stats.activeUsers != null ? stats.activeUsers.toLocaleString() : '…'} active`}
            />
            <MCard
              label="Paid Users" color="var(--green)" badge="ACTIVE"
              value={stats.paidUsers.toLocaleString()}
              sub={`${winPct}% conversion rate`}
            />
            <MCard
              label="Commission Earned" color="var(--blue)" badge="TOTAL"
              value={fmt$(stats.totalCommission)}
              sub="From active referrals"
            />
            <MCard
              label="Closed Trades" color="var(--gold)" badge="CLOSED"
              value={stats.closedTrades.toLocaleString()}
              sub="Closed trade count"
            />
            <MCard
              label="Total Referrals" color="var(--green)" badge="ALL"
              value={stats.totalReferrals.toLocaleString()}
              sub={`${stats.activeReferrals} converted`}
            />
          </>
        )}
      </div>

      {/* ── BODY ── */}
      <div className="ov-body">

        {/* LEFT column */}
        <div className="ov-left-col">

          {/* Growth chart */}
          <div className="panel">
            <div className="panel-hd">
              <div className="panel-title"><b>User</b> Growth</div>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <div style={{ display:'flex', background:'rgba(255,255,255,.05)', borderRadius:5, padding:2, gap:2 }}>
                  {[
                    { key:'line', title:'Line Chart', icon:(
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <polyline points="1,11 4,6 7,8 10,3 13,5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" fill="none"/>
                      </svg>
                    )},
                    { key:'bar', title:'Bar Chart', icon:(
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <rect x="1" y="7" width="3" height="6" rx="1" fill="currentColor" opacity=".9"/>
                        <rect x="5.5" y="4" width="3" height="9" rx="1" fill="currentColor" opacity=".9"/>
                        <rect x="10" y="2" width="3" height="11" rx="1" fill="currentColor" opacity=".9"/>
                      </svg>
                    )},
                    { key:'area', title:'Area Chart', icon:(
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M1,11 L4,6 L7,8 L10,3 L13,5 L13,13 L1,13 Z" fill="currentColor" opacity=".35"/>
                        <polyline points="1,11 4,6 7,8 10,3 13,5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" fill="none"/>
                      </svg>
                    )},
                  ].map(({ key, title, icon }) => (
                    <button
                      key={key}
                      title={title}
                      onClick={() => setChartType(key)}
                      style={{
                        background: chartType === key ? 'var(--gold)' : 'transparent',
                        color: chartType === key ? 'var(--black,#111)' : 'var(--gray)',
                        border: 'none', cursor: 'pointer', borderRadius: 3,
                        width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all .15s',
                      }}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
                <span style={{ fontSize:11, color:'var(--gray)' }}>Last 6 months</span>
              </div>
            </div>

            {growthValues.length ? (() => {
              const W = 280, H = 100, padB = 16, padT = 6, padX = 8;
              const min = Math.min(...growthValues), max = Math.max(...growthValues);
              const range = max - min || 1;

              const pts = growthValues.map((v, i) => [
                growthValues.length === 1
                  ? W / 2
                  : padX + (i / (growthValues.length - 1)) * (W - padX * 2),
                padT + (1 - (v - min) / range) * (H - padB - padT),
              ]);
              const linePath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
              const areaPath = `${linePath} L${pts[pts.length-1][0].toFixed(1)},${H-padB} L${pts[0][0].toFixed(1)},${H-padB} Z`;

              return (
                <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', height:H }}>
                  <defs>
                    <linearGradient id="growthGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="rgba(212,168,67,.28)"/>
                      <stop offset="100%" stopColor="rgba(212,168,67,0)"/>
                    </linearGradient>
                  </defs>
                  {[0.25, 0.5, 0.75].map(t => {
                    const y = padT + t * (H - padB - padT);
                    return <line key={t} x1={padX} x2={W-padX} y1={y} y2={y} stroke="rgba(255,255,255,.05)" strokeWidth="1"/>;
                  })}

                  {chartType === 'bar' && growthValues.map((v, i) => {
                    const barMax = Math.max(...growthValues) || 1;
                    const barH = (v / barMax) * (H - padB - padT);
                    const bw = (W - padX * 2) / Math.max(growthValues.length, 1);
                    const x = padX + i * bw + bw * 0.1;
                    return (
                      <g key={i}>
                        <rect x={x} y={H - padB - barH} width={bw * 0.8} height={barH} rx="2"
                          fill={`rgba(212,168,67,${0.4 + (i / Math.max(growthValues.length - 1, 1)) * 0.55})`}/>
                        <text x={x + bw * 0.4} y={H-2} fontSize="7.5" fill="rgba(247,243,234,.4)" textAnchor="middle">
                          {growthLabels[i]}
                        </text>
                      </g>
                    );
                  })}

                  {chartType === 'area' && (
                    pts.length === 1 ? (
                      <g>
                        <circle cx={pts[0][0]} cy={pts[0][1]} r="4" fill="var(--gold)" stroke="#111" strokeWidth="1.5"/>
                        <text x={pts[0][0]} y={H-2} fontSize="7.5" fill="rgba(247,243,234,.4)" textAnchor="middle">{growthLabels[0]}</text>
                      </g>
                    ) : (
                      <>
                        <path d={areaPath} fill="url(#growthGrad)"/>
                        <path d={linePath} fill="none" stroke="var(--gold)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round"/>
                        {pts.map((p, i) => (
                          <g key={i}>
                            <circle cx={p[0]} cy={p[1]} r="3" fill="var(--gold)" stroke="#111" strokeWidth="1.5"/>
                            <text x={p[0]} y={H-2} fontSize="7.5" fill="rgba(247,243,234,.4)" textAnchor="middle">{growthLabels[i]}</text>
                          </g>
                        ))}
                      </>
                    )
                  )}

                  {chartType === 'line' && (
                    pts.length === 1 ? (
                      <g>
                        <circle cx={pts[0][0]} cy={pts[0][1]} r="4" fill="var(--gold)" stroke="#111" strokeWidth="1.5"/>
                        <text x={pts[0][0]} y={H-2} fontSize="7.5" fill="rgba(247,243,234,.4)" textAnchor="middle">{growthLabels[0]}</text>
                      </g>
                    ) : (
                      <>
                        <path d={linePath} fill="none" stroke="var(--gold)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round"/>
                        {pts.map((p, i) => (
                          <g key={i}>
                            <circle cx={p[0]} cy={p[1]} r="3" fill="var(--gold)" stroke="#111" strokeWidth="1.5"/>
                            <text x={p[0]} y={H-2} fontSize="7.5" fill="rgba(247,243,234,.4)" textAnchor="middle">{growthLabels[i]}</text>
                          </g>
                        ))}
                      </>
                    )
                  )}
                </svg>
              );
            })() : (
              <div style={{ height:100, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--gray)', fontSize:12 }}>
                Not enough data yet
              </div>
            )}
          </div>

          {/* Subscription split */}
          <div className="panel">
            <div className="panel-hd">
              <div className="panel-title"><b>Subscription</b> Breakdown</div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:18 }}>
              {[
                { label:'Paid', count: stats?.paidUsers || 0,  color:'var(--gold)'  },
                { label:'Free', count: stats?.freeUsers || 0,  color:'var(--gray)'  },
              ].map(item => (
                <div key={item.label} style={{ background:'var(--dark3)', padding:'12px 14px', borderTop:`2px solid ${item.color}` }}>
                  <div style={{ fontSize:10, letterSpacing:'.06em', textTransform:'uppercase', color:'var(--gray)', marginBottom:6, fontWeight:600 }}>{item.label}</div>
                  <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:28, color:item.color, lineHeight:1 }}>
                    {loadingS ? '…' : item.count.toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ height:8, display:'flex', borderRadius:4, overflow:'hidden', gap:2 }}>
              <div style={{ width:`${winPct}%`, background:'var(--gold)' }}/>
              <div style={{ flex:1, background:'rgba(255,255,255,.08)' }}/>
            </div>
            <div style={{ fontSize:11, color:'var(--gray)', marginTop:6 }}>
              {winPct}% paid · {100 - winPct}% free
            </div>
          </div>

          {/* Trades quick stat */}
          <div className="panel">
            <div className="panel-hd">
              <div className="panel-title"><b>Trade</b> Activity</div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              {[
                { l:'Total Trades',  v: stats?.totalTrades,  c:'var(--gold)'  },
                { l:'Closed Trades', v: stats?.closedTrades, c:'var(--green)' },
              ].map(({ l, v, c }) => (
                <div key={l} style={{ background:'var(--dark3)', padding:'12px 14px' }}>
                  <div style={{ fontSize:10, letterSpacing:'.05em', textTransform:'uppercase', color:'var(--gray)', marginBottom:4, fontWeight:600 }}>{l}</div>
                  <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:26, color:c, lineHeight:1 }}>
                    {loadingS ? '…' : (v || 0).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT — paid users live feed */}
        <div className="ov-right-col">
          <div className="panel" style={{ flex:1 }}>
            <div className="panel-hd" style={{ marginBottom:6 }}>
              <div className="panel-title"><b>Recent</b> Paid Users</div>
              <span style={{ display:'flex', alignItems:'center', gap:5, fontSize:10, fontWeight:700, color:'var(--green)', letterSpacing:'.06em', background:'rgba(46,204,138,.08)', border:'1px solid rgba(46,204,138,.2)', padding:'2px 8px', borderRadius:20 }}>
                <span style={{ width:5, height:5, borderRadius:'50%', background:'var(--green)', animation:'pulseDot 2s ease infinite' }}/>
                LIVE
              </span>
            </div>

            {loadingU ? (
              <div style={{ color:'var(--gray)', fontSize:12, padding:'20px 0' }}>Loading…</div>
            ) : paidUsers.length ? (
              paidUsers.map((u, i) => (
                <PaidRow
                  key={u.id}
                  name={u.name}
                  plan={u.planName || 'Pro'}
                  amount="—"
                  time={new Date(u.joinedAt).toLocaleDateString()}
                  color={AVATAR_COLORS[i % AVATAR_COLORS.length]}
                />
              ))
            ) : (
              <div style={{ color:'var(--gray)', fontSize:12, padding:'20px 0' }}>No paid users yet.</div>
            )}

            {/* Quick stats grid */}
            {!loadingS && stats && (
              <div style={{ marginTop:20, paddingTop:16, borderTop:'1px solid rgba(255,255,255,.04)' }}>
                <div className="ov-quick-grid">
                  {[
                    ['Referrals',   stats.totalReferrals,  'var(--gold)' ],
                    ['Converted',   stats.activeReferrals, 'var(--green)'],
                    ['Total Users', stats.totalUsers,      'var(--blue)' ],
                    ['Trades',      stats.totalTrades,     'var(--gray)' ],
                  ].map(([l, v, c]) => (
                    <div key={l} style={{ background:'var(--dark3)', padding:'10px 12px' }}>
                      <div style={{ fontSize:10, letterSpacing:'.05em', textTransform:'uppercase', color:'var(--gray)', marginBottom:4, fontWeight:600 }}>{l}</div>
                      <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, color:c, lineHeight:1 }}>
                        {(v || 0).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

      </div>{/* /ov-body */}
    </div>
  );
}